/**
 * Enhanced session tracking for HelpPetAI
 * Tracks user journey throughout their entire session
 * 
 * Features:
 * - 24-hour tracking window (expires after 24 hours from first click)
 * - Sends email notification for each page view
 * - Integrates with Google Analytics
 * - Automatic cleanup after expiration
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const gtmPush = (payload: Record<string, any>) => {
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push(payload);
};

interface PageView {
  page: string;
  timestamp: string;
  time_on_page?: number;
}

interface SessionData {
  hp: number;
  session_id: string;
  started_at: string;
  pages_viewed: PageView[];
  referrer?: string;
  user_agent: string;
}

// Generate a unique session ID
const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create session data
const getSessionData = (hp?: number): SessionData | null => {
  const stored = sessionStorage.getItem('helppetai_session');
  
  if (stored) {
    const data: SessionData = JSON.parse(stored);
    
    // Check if session is older than 24 hours
    const sessionAge = Date.now() - new Date(data.started_at).getTime();
    const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    if (sessionAge > MAX_AGE) {
      // Session expired, clear it
      console.log(`Session expired (${Math.round(sessionAge / (60 * 60 * 1000))} hours old). Clearing...`);
      sessionStorage.removeItem('helppetai_session');
      return null;
    }
    
    return data;
  }
  
  if (hp) {
    // Create new session
    const newSession: SessionData = {
      hp,
      session_id: generateSessionId(),
      started_at: new Date().toISOString(),
      pages_viewed: [],
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent
    };
    sessionStorage.setItem('helppetai_session', JSON.stringify(newSession));
    console.log(`New session started for campaign #${hp}`);
    return newSession;
  }
  
  return null;
};

// Update session with new page view
const updateSessionPageView = (page: string) => {
  const sessionData = getSessionData();
  if (!sessionData) return;
  
  const now = new Date().toISOString();
  
  // Update time spent on previous page
  if (sessionData.pages_viewed.length > 0) {
    const lastPage = sessionData.pages_viewed[sessionData.pages_viewed.length - 1];
    const timeSpent = Date.now() - new Date(lastPage.timestamp).getTime();
    lastPage.time_on_page = Math.round(timeSpent / 1000); // in seconds
  }
  
  // Add new page view
  sessionData.pages_viewed.push({
    page,
    timestamp: now
  });
  
  sessionStorage.setItem('helppetai_session', JSON.stringify(sessionData));
  
  // Send tracking data to backend
  sendSessionUpdate(sessionData);
};

// Send session data to backend
const sendSessionUpdate = (sessionData: SessionData) => {
  // Double-check session age before sending (extra safety)
  const sessionAge = Date.now() - new Date(sessionData.started_at).getTime();
  const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours
  
  if (sessionAge > MAX_AGE) {
    console.log('⏰ Session tracking stopped - 24 hour limit reached');
    sessionStorage.removeItem('helppetai_session');
    return;
  }
  
  const hoursRemaining = Math.round((MAX_AGE - sessionAge) / (60 * 60 * 1000));
  
  fetch(`${API_BASE_URL}/api/v1/sales-tracking/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sessionData),
  })
    .then((response) => {
      if (response.ok) {
        console.log(`📊 Campaign #${sessionData.hp} - Page ${sessionData.pages_viewed.length} (${hoursRemaining}h tracking left)`);
      }
    })
    .catch((error) => {
      console.error('Session tracking error:', error);
    });
};

/**
 * Hook to track user sessions throughout their visit
 */
export const useSessionTracking = () => {
  const location = useLocation();
  const lastPage = useRef<string>('');

  useEffect(() => {
    // Parse query parameters
    const searchParams = new URLSearchParams(location.search);
    const hpParam = searchParams.get('hp');
    
    // Initialize session if hp parameter exists
    if (hpParam) {
      const hp = parseInt(hpParam, 10);
      if (hp >= 1 && hp <= 10000000) {
        getSessionData(hp);
        
        // Send campaign click to GTM / GA4 via dataLayer
        gtmPush({ campaign_id: hp });
        gtmPush({
          event: 'campaign_click',
            campaign_id: hp,
            page: location.pathname,
          referrer: document.referrer,
          });
      }
    }
    
    // Track page view if we have an active session
    const currentPage = location.pathname;
    if (currentPage !== lastPage.current) {
      updateSessionPageView(currentPage);
      lastPage.current = currentPage;
    }
  }, [location]);

  // Send final session data when user leaves
  useEffect(() => {
    const handleBeforeUnload = () => {
      const sessionData = getSessionData();
      if (sessionData && sessionData.pages_viewed.length > 0) {
        // Update time on last page
        const lastPage = sessionData.pages_viewed[sessionData.pages_viewed.length - 1];
        const timeSpent = Date.now() - new Date(lastPage.timestamp).getTime();
        lastPage.time_on_page = Math.round(timeSpent / 1000);
        
        // Use sendBeacon for reliable last-moment tracking
        const blob = new Blob([JSON.stringify(sessionData)], { type: 'application/json' });
        navigator.sendBeacon(`${API_BASE_URL}/api/v1/sales-tracking/session`, blob);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
};

/**
 * Track custom events (button clicks, form submissions, etc.)
 */
export const trackEvent = (eventName: string, eventData?: Record<string, any>) => {
  const sessionData = getSessionData();
  if (!sessionData) return;

  fetch(`${API_BASE_URL}/api/v1/sales-tracking/event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      hp: sessionData.hp,
      session_id: sessionData.session_id,
      event_name: eventName,
      event_data: eventData,
      page: window.location.pathname,
      timestamp: new Date().toISOString()
    }),
  }).catch(error => console.error('Event tracking error:', error));
};

